import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import LocationCard from './components/LocationCard';
import { locations } from '@/mocks/locations';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://britfitmobiletyres.co.uk';

const locationsPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'BritFit Mobile Tyres — Service Locations',
  description: 'Find your nearest BritFit Mobile Tyres location across the North of England.',
  url: `${SITE_URL}/locations`,
  numberOfItems: locations.length,
  itemListElement: locations.map((loc, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: `BritFit Mobile Tyres ${loc.city}`,
    url: `${SITE_URL}/locations/${loc.slug}`,
  })),
};

export default function LocationsPage() {
  useEffect(() => {
    document.title = 'Mobile Tyre Fitting Locations | BritFit Mobile Tyres UK';
    const desc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (desc) desc.content = 'Find your nearest BritFit Mobile Tyres location. We cover Middlesbrough, Durham, Hartlepool, Hull, Sheffield and more across the North of England.';

    const script = document.createElement('script');
    script.id = 'ld-locations-list';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(locationsPageSchema);
    document.head.appendChild(script);

    return () => {
      document.getElementById('ld-locations-list')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0F2A1D] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8b5ddb77162d926f64afad520e104ab1.jpg"
            alt="BritFit Mobile Tyres service locations across England"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F2A1D]/60 via-[#0F2A1D]/50 to-[#0F2A1D]/80" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-[#6EE7B7] text-xs font-semibold px-4 py-1.5 rounded-full mb-6 border border-white/10">
            <i className="ri-map-pin-line" /> {locations.length} Locations Across England
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">
            Find Your Nearest<br />
            <span className="text-[#6EE7B7]">BritFit Mobile Tyres</span>
          </h1>
          <p className="text-stone-300 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Professional mobile tyre fitting, puncture repair and wheel balancing — delivered to your door across the North of England.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-5 py-2.5 rounded-full border border-white/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" /> Back to Home
            </Link>
          </div>
        </div>
      </section>

      {/* Locations Grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3">Our Service Locations</h2>
          <p className="text-stone-500 text-sm md:text-base max-w-xl mx-auto">
            Each location is staffed by trained mobile tyre technicians ready to come to you — at home, work, or roadside.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((loc) => (
            <LocationCard key={loc.id} location={loc} />
          ))}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-[#1B4332] py-14 px-4 md:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Not sure which location to call?
          </h2>
          <p className="text-stone-300 text-sm md:text-base mb-8">
            Call our main line and we&apos;ll connect you with the nearest available technician.
          </p>
          <a
            href="tel:+441234567890"
            className="inline-flex items-center gap-2 bg-white text-[#1B4332] text-sm font-bold px-8 py-3.5 rounded-full hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line" /> Call BritFit Now
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
